import { NgTemplateOutlet } from "@angular/common"
import {
	Component,
	EventEmitter,
	Input,
	Output,
	booleanAttribute,
	ChangeDetectionStrategy,
} from "@angular/core"
import { CanvasWriteComponent } from "@app/canvas-write/canvas-write.component"
import { COMMON_IMPORTS } from "@app/common-imports"
import { Test } from "@app/definitions/types"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { GenTableDisplayComponent } from "@app/gen-table-display/gen-table-display.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { Modalable } from "@app/Modalable"
import { TestSchedulerComponent } from "@app/test-scheduler/test-scheduler.component"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { SafeHtmlPipe } from "@app/utils/safe-html.pipe"
import { BtnExtComponent, CancelBtnComponent } from "@app/utils/utils.component"

@Component({
	selector: "multi",
	imports: [COMMON_IMPORTS],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@for (opt of options; track opt) {
			<p class="d-flex flex-nowrap align-items-start gap-2">
				<input
					type="checkbox"
					(click)="handleCheck(opt.value)"
					[checked]="arr.includes(opt.value)"
				/>
				{{ opt.value }}
			</p>
		}
	`,
})
export class Multi {
	@Input() options: any[]
	@Input() value: any
	@Output() onChange = new EventEmitter()
	arr: string[] = []

	ngOnInit() {
		this.arr = this.value.split(",").map((it) => it.trim())
	}
	handleCheck(value: string) {
		this.arr = (
			this.arr.includes(value)
				? this.arr.filter((it) => it != value)
				: [...this.arr, value]
		).filter(Boolean)
		this.onChange.emit(this.arr.join(", "))
	}
}

@Component({
	selector: "app-testanswers-fill",
	imports: [
		COMMON_IMPORTS,
		NgTemplateOutlet,
		CanvasWriteComponent,
		LangValPipe,
		CancelBtnComponent,
		BtnExtComponent,
		SafeHtmlPipe,
		GenInputComponent,
		GenButtonGroupComponent,
		ModalWrapComponent,
		TestSchedulerComponent,
		GenTableDisplayComponent,
		Multi,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./testanswers-fill.component.html",
})
export class TestanswersFillComponent extends Modalable {
	modalProps: any = null
	isSaving: boolean = false
	isSendingInvite: boolean = false

	@Input() test_id: number = 0
	@Input() patient_id: number = 0
	@Input() state: string
	@Input({ transform: booleanAttribute }) inUserPatientMode: boolean = false
	@Input({ transform: booleanAttribute }) in_followup: boolean = false
	@Output() onFinish = new EventEmitter()
	@Output() onSaveState = new EventEmitter()
	test: any = null
	patient: any = null
	isFormSubmittable: boolean = false
	user_sign_img_blob: any = null
	userSignErr: string = ""
	patient_sign_img_blob: any = null
	patientSignErr: string = ""

	saveInt: any = null

	ngOnDestroy() {
		clearInterval(this.saveInt)
	}
	ngOnInit() {
		if (this.patient_id) {
			this.patient = this.store.searchSiteDataItem(this.patient_id, [
				"patients",
			])
		}
		this.test = this.store.searchSiteDataItem(this.test_id, ["tests"])
		this.test.tests = this.store
			.getSiteDataTable("tests")
			.filter((tq) => tq.test_id == this.test_id)
		this.test.tests.sort((a, b) => a.order_num - b.order_num)
		if (this.test.tests.length) {
			this.test.tests.forEach((t) => this.prepQustionsForTest(t))
		} else {
			this.prepQustionsForTest(this.test)
		}

		if (this.state) {
			const questions = JSON.parse(this.state)
			questions.forEach(({ id, answer }) => {
				this.prepTestQAns(this.test, id, answer)
				if (this.test.tests?.length) {
					this.test.tests.forEach((t) => this.prepTestQAns(t, id, answer))
				}
			})
		}

		if (this.onSaveState) {
			this.saveInt = setInterval(() => {
				this.onSaveState.emit(this.test)
			}, 10 * 1000)
		}
	}
	prepTestQAns(test: any, qId: number, answer: any) {
		if (!test.questions?.length) {
			return
		}
		const tq = test.questions.find((it) => it.id == qId)
		if (!tq) {
			return
		}
		tq.answer = answer
		this.validateEntireForm()
		if (!answer) {
			return
		}
		switch (tq.field_type) {
			case "opts-checkbox":
				return this.onCheck(
					tq,
					tq.optionsArr.find((opt) => opt.value == answer)
				)
			case "opts-btn-group":
				return this.onBtnGroupClick(tq)
		}
	}

	prepQustionsForTest(test: any) {
		test.questions = this.store
			.getSiteDataTable("testquestions")
			.filter((tq) => tq.test_id == test.id)
		test.questions.sort((a, b) => a.order_num - b.order_num)
		test.questions.forEach((tq) => {
			//tq.mark = null
			tq.answer = tq.field_type == "number" ? 0 : ""
			tq.errors = {}
			if (tq.options?.length) {
				const opts = JSON.parse(tq.options)
				tq.optionsArr = opts
				// .map((op, pos) => ({
				// 	...op,
				// 	id: pos + 1,
				// 	name: op.value,
				// }))
			}
		})
	}
	validateEntireForm() {
		this.isFormSubmittable = true
		if (this.test.tests.length) {
			this.test.tests.forEach((t) => this.validateQuestions(t))
		} else {
			this.validateQuestions(this.test)
		}
		if (this.test?.user_sign_required == "yes") {
			this.userSignErr = ""
			if (!this.user_sign_img_blob) {
				this.userSignErr = this.lang.getVal("test_should_be_signed")
				this.isFormSubmittable = false
			}
		}
		if (this.test?.patient_sign_required == "yes") {
			this.patientSignErr = ""
			if (!this.patient_sign_img_blob) {
				this.patientSignErr = this.lang.getVal("test_should_be_signed")
				this.isFormSubmittable = false
			}
		}
	}
	validateQuestions(test: any) {
		for (let tq of test.questions) {
			if (tq.required == "no") {
				continue
			}
			tq.errors = {}
			const validationRules: any = { answer: { not_empty: true }, mark: {} }
			if (tq.field_type === "number") {
				validationRules.answer = { not_empty: true, is_integer: true }
				if (tq.range_min) {
					validationRules.answer.larger_than = Number(tq.range_min) - 1
					validationRules.answer.smaller_than = Number(tq.range_max) + 1
				}
			}
			const isOk = this.validator.validateEntireForm(
				tq,
				tq.errors,
				validationRules
			)
			if (!isOk) {
				this.isFormSubmittable = false
			}
		}
	}

	saveUserPatient() {
		const sendObj: any = { ...this.test }
		if (this.test?.user_sign_required == "yes") {
			sendObj.user_sign_img_blob = this.user_sign_img_blob
		}
		if (this.test?.patient_sign_required == "yes") {
			sendObj.patient_sign_img_blob = this.patient_sign_img_blob
		}
		this.onClose.emit(sendObj)
		this.onFinish.emit(sendObj)
	}

	async save() {
		this.isSaving = true

		const sendObj: any = {
			...this.test,
			patient_id: this.patient_id,
			in_followup: this.in_followup,
		}
		if (this.test?.user_sign_required == "yes") {
			sendObj.user_sign_img_blob = this.user_sign_img_blob
		}
		if (this.test?.patient_sign_required == "yes") {
			sendObj.patient_sign_img_blob = this.patient_sign_img_blob
		}
		await this.store.downloadToastFunc(async () => {
			const resPdf = await this.apiService.download("answer_test", sendObj)
			if (!resPdf?.size) {
				return this.modalService.openToast("save_failed")
			}
			this.apiService.downloadFile(resPdf, this.lang.getVal("test") + ".pdf")
		})
		this.modalService.openToast("all_answers_filled_saved")
		this.isSaving = false

		let returnText = this.getTestText(this.test)
		this.store.detectMixedListChangeNeeded("testfills", this.patient_id)

		this.onClose.emit(returnText)
		this.onFinish.emit(returnText)
	}
	getTestText(test: any) {
		let retText =
			"<span class='fw-bold  text-decoration-underline'>" +
			test.name +
			"</span> <br />"
		if (test?.tests?.length > 1) {
			test.tests.forEach((t) => {
				retText += this.getTestText(t)
			})
		}
		if (test?.questions?.length) {
			test.questions.forEach((tq) => {
				retText +=
					"<div style='font-weight: 700'>" +
					tq.question +
					"</div><div style='font-weight: 500; padding-bottom: 10px'>" +
					tq.answer +
					"</div>"
			})
		}
		return retText
	}
	async send_invite(arr: any[]) {
		this.isSendingInvite = true
		await this.store.sendInviteCommon(
			arr,
			this.patient.id,
			{
				test_ids: [this.test_id],
			},
			true
		)
		this.isSendingInvite = false
		this.onClose.emit(true)
	}
	// sendInviteWa(){
	//   this.store.patientSendInviteWa(this.patient.id,[this.test_id]);
	// }
	onCheck(tq: any, opt: any) {
		tq.answer = opt.value
		if (this.test.calculated === "yes") {
			tq.mark = opt.mark
		}
		this.validateEntireForm()
	}
	onBtnGroupClick(tq: any) {
		if (this.test.calculated === "yes") {
			const opt = tq.optionsArr.find((it) => it.value === tq.answer)
			tq.mark = opt?.mark ?? null
		}
		this.validateEntireForm()
	}
}
